// @/utils/changeDetection.js
import { appStore } from '@/services/app/store';

/**
 * Simple utility to check for unsubmitted changes in business segments and company questions
 * 
 * Provides reliable detection of draft content in self-assessment data
 * with status-based tracking for maximum reliability.
 * 
 * Features:
 * - Direct status detection for accurate results
 * - Cross-component draft detection
 * - Session-based user activity tracking
 * - Consolidated status reporting
 */

/**
 * Check if there are any unsubmitted changes in the self-assessment
 * Uses assessmentStatus as primary indicator instead of timestamp comparison
 * 
 * @returns {boolean} True if there are unsubmitted changes
 */
export function hasUnsubmittedChanges() {
  const customerRequests = appStore.state.data.customerRequests?.requests || [];
  const acceptedRequests = customerRequests.filter(
    req => req.Status?.toLowerCase() === 'accepted'
  );

  // Check company questions (always checked)
  const companyQuestions = appStore.state.data.companyQuestions?.data;
  if (companyQuestions?.assessmentStatus === 'draft') {
    return true;
  }

  // Check only business segments that have accepted requests
  const businessSegments = appStore.state.data.businessSegments?.bySector || {};
  for (const [, segments] of Object.entries(businessSegments)) {
    for (const segment of segments) {
      // Only check if there's an accepted request for this segment
      const hasAcceptedRequest = acceptedRequests.some(
        req => req.RequestedSector === segment.sectorCode &&
          req.RequestedCountry === segment.countryCode
      );

      if (hasAcceptedRequest && segment.assessmentStatus === 'draft') {
        return true;
      }
    }
  }

  // Check for accepted requests that have never had anything submitted
  for (const req of acceptedRequests) {
    if (!req.LastSubmitDate) {
      return true;
    }
  }

  return false;
}

/**
 * Track when a user makes a change by setting a timestamp in session storage
 * Called from component update handlers to mark user activity
 */
export function trackUserChange() {
  if (typeof window !== 'undefined' && sessionStorage) {
    try {
      const timestamp = new Date().toISOString();
      sessionStorage.setItem('lastUserChangeTimestamp', timestamp);
    } catch (e) {
      // Silent error in production
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to track user change:', e);
      }
    }
  }
}

/**
 * Clear user change tracking after submission
 * Called after successful submission to reset change tracking
 */
export function clearChangeTracking() {
  if (typeof window !== 'undefined' && sessionStorage) {
    try {
      sessionStorage.removeItem('lastUserChangeTimestamp');
    } catch (e) {
      // Silent error in production
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to clear change tracking:', e);
      }
    }
  }
}